import type { Status } from "@mailchimp/mailchimp_marketing";
import mailchimp from "@mailchimp/mailchimp_marketing";

mailchimp.setConfig({
  apiKey: "1515c7487a831699b74434187644f20b-us5",
  server: "us5",
});

export const waitlistId = "825826f862";

export const makeMailchimpClient = (config: any) => {
  async function addToWaitlistCampaign(email: string, tags?: string[]) {
    const lists = await mailchimp.lists.addListMember(
      mcResourceLists[config.isDeployedToProd ? "waitlist" : "test"],
      {
        email_address: email,
        status: "subscribed" as Status,
        tags: ["areahub.com", "main-site", "waitlist", ...(tags || [])],
      },
    );
    return lists;
  }

  const mcDataDefs = {
    "first-name": "FNAME",
    "last-name": "LNAME",
    address: "ADDRESS",
    phone: "PHONE",
  };

  const mcResourceLists = {
    "resource-request-list": "032f232c51",
    waitlist: "825826f862",
    test: "4b8d339ec6",
  };

  function appendDataDefs(formData: any) {
    const data: any = {};
    for (const [key, value] of Object.entries(formData)) {
      if (mcDataDefs[key as keyof typeof mcDataDefs]) {
        data[mcDataDefs[key as keyof typeof mcDataDefs]] = value;
      }
    }
    return {
      ...data,
      HCL_BSENT: "false",
      HCL_RSENT: "false",
      PGR_SENT: "false",
    };
  }

  type MemberStatus = Status | "unadded";

  async function getMemberStatus(
    listId: string,
    email_address: string,
  ): Promise<MemberStatus> {
    return await mailchimp.lists
      .getListMember(listId, email_address, {
        fields: ["status"],
        excludeFields: [],
      })
      .then((response: { status: Status }) => response.status)
      .catch(() => "unadded");
  }

  /**
   * Triggers custom event that sends an email based on the target event
   * The only difference b/w events w/in the same list is the event's target name
   */
  async function triggerMemberEvent(
    listId: string,
    emailAddress: string,
    target: string,
  ) {
    return await mailchimp.lists
      .createListMemberEvent(listId, emailAddress, { name: target })
      .then(() => ({ status: 200 }))
      .catch((error: any) => error);
  }

  async function sendResourceEmail(resourceKey: string, formData: any) {
    const emailAddress = formData.email ?? formData["email-address"];
    const listId = mcResourceLists["resource-request-list"];

    // Get the email's member status in the list
    // We can only trigger events for audience members
    // And would like to trigger only for those that have subscribed to receive mail
    const memberStatus = await getMemberStatus(listId, emailAddress);
    switch (memberStatus) {
      case "unadded":
        // The email has not yet subscribed. Subscribe first, then run case: "subscribed"
        const merge_fields = appendDataDefs(formData);
        try {
          await mailchimp.lists.addListMember(listId, {
            email_address: emailAddress,
            status: "subscribed" as Status,
            tags: [resourceKey, "main-site", "areahub.com", "resource-request"],
            merge_fields,
          });
        } catch (error: any) {
          const responseBody = error?.response?.body;
          console.error({
            Error: `Error adding list member request: list_id: ${listId}, email: ${emailAddress}`,
            ...responseBody,
          });
          // Go to jail, and do not pass go.
          break;
        }
      case "subscribed":
        // The member has already subscribed to the list. Trigger the email event.
        const resourceType =
          formData["housing-type"] ?? formData["resource-type"];
        const apiTarget = formData["housing-type"]
          ? `housing-checklist-${resourceType}`
          : `${resourceType}`;
        try {
          await triggerMemberEvent(listId, emailAddress, apiTarget);
          return { status: 200 };
        } catch (error: any) {
          const responseBody = error?.response?.body;
          console.error({
            Error: `Error triggering member event list_id: ${listId}, email: ${emailAddress}, apiTarget: ${apiTarget}`,
            ...responseBody,
          });
        }
      case "unsubscribed":
        return {
          status: 400,
          title:
            "This email address has indicated it would like to stop receiving mail from us. Please update your subscriber preferences or try again with a different email.",
        };
      default:
    }
    return {
      status: 400,
      title:
        "There was an error completing your request. Please try again later, or with a different email.",
    };
  }

  const getAllLists = async () => {
    // @ts-ignore
    const response = await mailchimp.lists.getAllLists();
    return response;
  };

  const addMergeFields = async (
    fieldName: string,
    fieldType:
      | "text"
      | "number"
      | "address"
      | "phone"
      | "date"
      | "url"
      | "imageurl"
      | "radio"
      | "dropdown"
      | "birthday"
      | "zip",
    options?: any,
  ) => {
    // @ts-ignore
    const field = await mailchimp.lists.addListMergeField(
      mcResourceLists[
        config.isDeployedToProd ? "resource-request-list" : "test"
      ],
      {
        name: fieldName,
        type: fieldType,
        options,
      },
    );
    return field;
  };
  return {
    mcDataDefs,
    mcResourceLists,
    appendDataDefs,
    getMemberStatus,
    addToWaitlistCampaign,
    triggerMemberEvent,
    sendResourceEmail,
    getAllLists,
    addMergeFields,
    mailchimp,
  };
};
